<template>
  <section v-if="links.length" class="related-mesh">
    <div class="mx-auto max-w-6xl px-4 py-10 sm:px-6 md:py-12">
      <div class="related-mesh-split">
        <h2>{{ heading }}</h2>
        <nav class="related-mesh-grid" :aria-label="`Related pages for ${title}`">
          <NuxtLink
            v-for="link in links"
            :key="link.path"
            :to="localePath(link.path)"
            class="related-mesh-link"
          >
            <span>{{ link.title }}</span>
            <span aria-hidden="true">→</span>
          </NuxtLink>
        </nav>
      </div>
    </div>
  </section>
</template>

<script setup>
import { relatedPagesFor } from '~/data/internal-links';

const props = defineProps({
  path: { type: String, required: true },
  title: { type: String, default: 'this page' },
  exclude: { type: Array, default: () => [] },
  limit: { type: Number, default: 5 },
  // Pages that already have their own related section pass a different heading.
  heading: { type: String, default: 'Related pages' },
});

const { localePath } = useWorkspaceLocale();
const links = computed(() => relatedPagesFor(props.path, props.limit, props.exclude));
</script>

<style scoped>
/* Dark: keeps the band's existing navy surface (#020617) and navy hairline so it blends with the navy host sections and Cta03; a transparent band exposes the white body on pages that do not paint their own dark background.
   Approved list language (learn hub, app neutral tokens): hairline 1px, 10px list surface, quiet arrow. */
.related-mesh {
  --ink: #0a0a0a;
  --ink3: #4f4f4f;
  --edge: #e6e6e6;
  --hover: rgb(10 10 10 / 0.04);
  border-top: 1px solid var(--edge);
  background: #ffffff;
  color: var(--ink);
}

.dark .related-mesh {
  --ink: #fafafa;
  --ink3: #9a9a9a;
  --edge: #243247;
  --hover: rgb(250 250 250 / 0.05);
  background: #020617;
}

.related-mesh-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
}

.related-mesh-split h2 {
  font-size: clamp(1.4rem, 2.6vw, 1.75rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.005em;
}

.related-mesh-grid {
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 10px;
}

.related-mesh-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-top: 1px solid var(--edge);
  padding: 0.85rem 1.1rem;
  color: var(--ink);
  font-size: 0.95rem;
  font-weight: 650;
  line-height: 1.35;
  text-decoration: none;
  transition: background-color 120ms ease;
}

.related-mesh-link:first-child {
  border-top: 0;
}

.related-mesh-link:hover,
.related-mesh-link:focus-visible {
  background: var(--hover);
}

.related-mesh-link > span:last-child {
  color: var(--ink3);
}

@media (min-width: 1024px) {
  .related-mesh-split {
    grid-template-columns: minmax(0, 22rem) minmax(0, 1fr);
    gap: 4rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .related-mesh-link {
    transition: none;
  }
}
</style>
